/**
 * NewConversationPage — Create DM or group conversation
 *
 * Adapted from bachata-nation-next NewConversation.tsx (425L).
 * User search via Vidnight users API, role display, neubrutalism styling.
 */

import { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import http from '@/network/http';
import { useAuthStore } from '@/features/auth';
import { useMessagingFlow } from '@/stackflow';
import { PageLayout } from '@/components/layout';
import { useCreateConversation } from '../hooks';
import { useBatchPresence } from '../hooks';
import { ChatAvatar } from '../components';

interface UserResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  avatarUrl?: string | null;
}

// =============================================================================
// User Item
// =============================================================================

interface UserItemProps {
  user: UserResult;
  selected: boolean;
  isOnline?: boolean;
  onQuickDm: () => void;
  onToggle: () => void;
}

const UserItem = memo(function UserItem({ user, selected, isOnline, onQuickDm, onToggle }: UserItemProps) {
  const { t } = useTranslation();
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 transition-all ${
        selected
          ? 'glass-tinted shadow-sm'
          : 'glass-surface hover:shadow-sm'
      }`}
      style={{
        borderColor: 'var(--user-page-card-border)',
        ...(selected ? {} : {}),
      }}
    >
      {/* Avatar + name area — click to start DM immediately */}
      <button
        type="button"
        onClick={onQuickDm}
        className="flex flex-1 items-center gap-3 overflow-hidden text-start"
      >
        {/* Avatar with online indicator */}
        <div className="relative flex-shrink-0">
          <ChatAvatar src={user.avatarUrl} name={fullName} />
          {isOnline && (
            <div
              className="absolute -bottom-0.5 -end-0.5 h-3.5 w-3.5 rounded-full border bg-success-500"
              style={{ borderColor: 'var(--user-page-card-border)' }}
            />
          )}
        </div>

        {/* User info */}
        <div className="flex-1 overflow-hidden" dir="auto">
          <div className="truncate text-sm font-medium" style={{ color: 'var(--user-page-heading)' }}>{fullName}</div>
          <div className="flex items-center gap-1.5">
            <span
              className="rounded border-[1px] px-1 py-0 text-[10px] font-medium"
              style={{
                borderColor: 'var(--user-page-card-border)',
                backgroundColor: 'var(--user-page-card-bg)',
                color: 'var(--user-page-text)',
              }}
            >
              {t(`roles.${user.role.toLowerCase()}`, user.role)}
            </span>
            <span className="truncate text-xs" style={{ color: 'var(--user-page-text)' }}>{user.email}</span>
          </div>
        </div>
      </button>

      {/* Checkbox — click to toggle multi-select */}
      <button
        type="button"
        onClick={onToggle}
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border transition-all ${
          selected
            ? 'text-white'
            : 'glass-surface'
        }`}
        style={{
          borderColor: 'var(--user-page-card-border)',
          ...(selected ? { backgroundColor: 'var(--user-page-heading)' } : {}),
        }}
        aria-label={selected ? t('messaging.deselect') : t('messaging.select')}
      >
        {selected && (
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 12 9 17 20 6" />
          </svg>
        )}
      </button>
    </motion.div>
  );
});

// =============================================================================
// Selected Chip
// =============================================================================

interface SelectedChipProps {
  user: UserResult;
  onRemove: () => void;
}

const SelectedChip = memo(function SelectedChip({ user, onRemove }: SelectedChipProps) {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return (
    <motion.div
      layout
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      className="flex items-center gap-1 rounded-full border py-0.5 pe-1 ps-2 text-xs font-medium"
      style={{
        borderColor: 'var(--user-page-card-border)',
        backgroundColor: 'var(--user-page-card-bg)',
        color: 'var(--user-page-heading)',
      }}
    >
      <span>{fullName}</span>
      <button
        type="button"
        onClick={onRemove}
        className="flex h-4 w-4 items-center justify-center rounded-full text-white transition-colors hover:bg-error-500"
        style={{ backgroundColor: 'var(--user-page-heading)' }}
      >
        <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </motion.div>
  );
});

// =============================================================================
// Main Page
// =============================================================================

export function NewConversationPage() {
  const { t } = useTranslation();
  const { pop, replace } = useMessagingFlow();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<UserResult[]>([]);
  const [groupName, setGroupName] = useState('');

  const createConversation = useCreateConversation();

  // Fetch users from messaging user search endpoint (any authenticated user)
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['messaging', 'users', searchQuery],
    queryFn: () =>
      http
        .get<{ users: UserResult[] }>('/messaging/users', {
          params: searchQuery.length >= 2 ? { search: searchQuery } : undefined,
        })
        .then((r) => r.data),
    staleTime: 30 * 1000,
  });

  const allUsers = usersData?.users || [];

  // Filter out current user
  const filteredUsers = useMemo(() => {
    return allUsers.filter((u) => u.id !== currentUserId);
  }, [allUsers, currentUserId]);

  // Sort: online first (use batch presence)
  const userIds = useMemo(() => filteredUsers.map((u) => u.id), [filteredUsers]);
  const { presenceMap } = useBatchPresence(userIds);

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aOnline = presenceMap[a.id]?.isOnline ? 1 : 0;
      const bOnline = presenceMap[b.id]?.isOnline ? 1 : 0;
      return bOnline - aOnline;
    });
  }, [filteredUsers, presenceMap]);

  const isGroup = selectedUsers.length >= 2;

  const handleToggleUser = useCallback((user: UserResult) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      if (exists) return prev.filter((u) => u.id !== user.id);
      return [...prev, user];
    });
  }, []);

  // Quick DM: click on avatar/name to immediately create a 1-on-1 conversation
  const handleQuickDm = useCallback(
    async (user: UserResult) => {
      try {
        const result = await createConversation.mutateAsync({
          type: 'dm',
          participantIds: [user.id],
        });
        replace('ChatActivity', { conversationId: result.conversation.id });
      } catch (error) {
        console.error('Failed to create DM:', error);
      }
    },
    [createConversation, replace]
  );

  const handleCreate = useCallback(async () => {
    if (selectedUsers.length === 0) return;

    try {
      const result = await createConversation.mutateAsync({
        type: isGroup ? 'group' : 'dm',
        name: isGroup ? groupName.trim() || undefined : undefined,
        participantIds: selectedUsers.map((u) => u.id),
      });

      replace('ChatActivity', { conversationId: result.conversation.id });
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  }, [selectedUsers, isGroup, groupName, createConversation, replace]);

  return (
    <PageLayout
      topBar={
        <>
          {/* Back button */}
          <button
            type="button"
            onClick={() => pop()}
            className="flex h-10 w-10 items-center justify-center rounded-full border transition-all active:scale-95"
            style={{
              borderColor: 'var(--user-page-card-border)',
              backgroundColor: 'var(--user-page-card-bg)',
            }}
          >
            <svg className="h-5 w-5 rtl:rotate-180" style={{ color: 'var(--user-page-heading)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <h1
            className="flex-1 text-center text-lg sm:text-2xl font-semibold tracking-wide"
            style={{ color: 'var(--user-page-heading)' }}
          >
            {t('messaging.newConversation')}
          </h1>

          {/* Spacer to balance the back button */}
          <div className="w-10" />
        </>
      }
      bottomBar={
        selectedUsers.length > 0 ? (
          <div className="px-4 py-3">
            <button
              type="button"
              onClick={handleCreate}
              disabled={createConversation.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl border py-3.5 text-base font-medium text-white transition-all active:scale-[0.98] disabled:opacity-60"
              style={{
                borderColor: 'var(--user-page-card-border)',
                backgroundColor: 'var(--user-page-heading)',
              }}
            >
              {createConversation.isPending ? (
                <>
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  {t('messaging.creating')}
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                  {isGroup ? t('messaging.createGroup') : t('messaging.startChat')}
                </>
              )}
            </button>
          </div>
        ) : undefined
      }
    >
      {/* Selected users chips */}
      <AnimatePresence>
        {selectedUsers.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-3 overflow-hidden"
          >
            <div
              className="flex flex-wrap gap-2 rounded-xl border p-2"
              style={{
                borderColor: 'var(--user-page-card-border)',
                backgroundColor: 'var(--user-page-card-bg)',
              }}
            >
              <AnimatePresence>
                {selectedUsers.map((user) => (
                  <SelectedChip
                    key={user.id}
                    user={user}
                    onRemove={() => handleToggleUser(user)}
                  />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Group name input (3+ participants) */}
      <AnimatePresence>
        {isGroup && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-3 overflow-hidden"
          >
            <style>{`
              .new-conv-group-input::placeholder { color: var(--user-page-text); }
            `}</style>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('messaging.groupNamePlaceholder')}
              dir="auto"
              className="new-conv-group-input w-full rounded-xl border glass-surface px-4 py-2.5 text-sm focus:shadow-sm focus:outline-none"
              style={{
                borderColor: 'var(--user-page-card-border)',
                color: 'var(--user-page-heading)',
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search bar */}
      <div className="relative mb-4">
        <svg
          className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2"
          style={{ color: 'var(--user-page-text)' }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <style>{`
          .new-conv-search::placeholder { color: var(--user-page-text); }
        `}</style>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('messaging.searchUsers')}
          dir="auto"
          className="new-conv-search w-full rounded-xl border glass-surface py-2.5 pe-4 ps-10 text-sm transition-all focus:shadow-sm focus:outline-none"
          style={{
            borderColor: 'var(--user-page-card-border)',
            color: 'var(--user-page-heading)',
          }}
        />
      </div>

      {/* Section title */}
      <h3 className="mb-2 text-xs font-semibold" style={{ color: 'var(--user-page-text)' }}>
        {searchQuery.length >= 2
          ? t('messaging.searchResults')
          : t('messaging.suggestedUsers')}
      </h3>

      {/* User list */}
      <div className="space-y-2">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-[64px] animate-pulse rounded-xl border"
              style={{
                borderColor: 'var(--user-page-card-border)',
                backgroundColor: 'var(--user-page-card-bg)',
              }}
            />
          ))
        ) : sortedUsers.length > 0 ? (
          sortedUsers.map((user) => (
            <UserItem
              key={user.id}
              user={user}
              selected={selectedUsers.some((u) => u.id === user.id)}
              isOnline={presenceMap[user.id]?.isOnline}
              onQuickDm={() => handleQuickDm(user)}
              onToggle={() => handleToggleUser(user)}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div
              className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border"
              style={{
                borderColor: 'var(--user-page-card-border)',
                backgroundColor: 'var(--user-page-card-bg)',
              }}
            >
              <svg className="h-6 w-6" style={{ color: 'color-mix(in srgb, var(--user-page-heading) 30%, transparent)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <p className="text-sm" style={{ color: 'var(--user-page-text)' }}>
              {searchQuery.length >= 2
                ? t('messaging.noUsersFound')
                : t('messaging.startTypingToSearch')}
            </p>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

export default NewConversationPage;
